import type { AddressInfo, MediaType, MetaDataObj } from 'src/helper/shared-types';
import type { Issue } from 'src/redux/issues/types';
import type { GroupedDataMetaItem } from 'src/redux/list-view/types';
import type { ResidentAvailabilityResponseType } from 'src/redux/service-request/types';
import type { MaterialObjTypeForApi } from 'src/views/WorkOrder/SidePanel/Trips/CloseTrip/type';
import type { SchedulingPreference } from 'src/views/WorkOrder/SidePanel/Trips/types';

type TagData = {
  tag_id: string;
  name: string;
  color: string;
  text_color: string;
  slug: string;
  time_used: number;
  [key: string]: any;
};

// smart nte settings
export type SmartNteConfigData = {
  smart_nte_max_value_in_cents: number;
};

export type TripSource = {
  trip_id: string;
  type?: 'trip' | 'request';
  schedule_start_time?: string;
  schedule_end_time?: string;
  en_route_at?: string;
  en_route_paused_at?: string;
  en_route_resumed_at?: string;
  total_drive_time_in_sec?: number;

  work_started_at?: string;
  work_completed_at?: string;
  work_paused_at?: string;
  work_resumed_at?: string;
  total_labor_time_in_sec?: number;

  additional_notes?: string;
  appointment?: {
    technician_id?: string;
    appointment_id?: string;
    name?: string;
    profile_pic?: string;
    type?: 'lula' | 'in-house' | 'third-party-vendor';
    vendor_instructions?: string;
  };
  is_active: boolean;
  status: {
    label: string;
    value: string;
  };
  trip_number: string;
  is_rescheduled: string;
  last_modified_at: string;
  abilities: Array<string>;

  is_canceled: boolean;
  work_performed: {
    label: string;
    value: string;
  };
  notified_vendor_count?: number;
  quotes?: {
    quote_id: number;
    status: {
      label: string;
      value: string;
    };
    public_url: string;
  }[];
  estimated_return_date?: string;
  canceled_reason?: string;
  paused_reason?: string;
  canceled_at?: string;
  is_under_qa?: boolean;
  arranged_return_date?: string;
  start_quality_check?: string;
  elapsed_time?: string;
};

export type WorkOrderTrips = {
  trip_ids: Array<string>;
  tripSource: {
    [key: string]: TripSource;
  };
  activeTripId: string;
};

export type MaterialTypeForModal = {
  id?: string;
  material_id?: string;
  label: string;
  quantity: string;
  materialCost: string;
  pricingMethod: 'total' | 'per-unit';
  quantityType: { value: string; label: string } | null;
  materialMarkupFee?: string;
  materialMarkupFeeType?: 'fixed' | 'percentage';
  totalCostIncludingMarkup?: number;
};

export type AddNewQuoteMaterialFormState = {
  id?: string;
  label: string;
  quantity: string;
  quantity_type: null | { value?: string; label?: string } | string;
  material_cost: string;
  pricing_method?: string;
};

export type MaterialFormattedPayload = AddNewQuoteMaterialFormState & {
  cost_in_cents?: number;
  unit_price_in_cents?: number;
  material_id?: string;
};
export type AssigneeItem = WorkOrderDataObj['assignees'][0] & { isSelected?: boolean };

export type QuoteApproveFormField = {
  selected_options: Record<string, string>;
  optional_selected_data: Record<string, string>;
  optional_selected: string[];
  trip_fee_type?: 'pay_trip_fee' | 'pay_if_rejected' | 'never_pay';
  trip_fee_amount_always?: string;
  trip_fee_amount_rejected?: string;
};

export type MaterialsAPIResponse = {
  material_id: string | null;
  label: string;
  material_cost_in_cents?: number;
  unit_price_in_cents?: number;
  cost_in_cents?: number;
  quantity: number;
  quantity_type: string;
  cost_type: string;
  markup_fee_type?: string;
  markup_fee_type_value?: string;
  markup_fee_in_cents?: number;
  total_cost_in_cents?: number;
};

export type Quote = {
  status: {
    label: string;
    value: string;
  };
  quote_id: string;
  quote_number: string;
  estimate_in_cents: number;
  approved_estimate_in_cents?: number;
  is_latest?: boolean;
  pdf_link?: string;
  action_details: {
    submitted_at?: string;
    submitted_by?: string;
    decided_at?: string;
    decided_by?: string;
  };
};

export type Appointment = {
  appointment_id: string;
  trip_number: number;
  start_time: string;
  end_time: string;
  technician?: {
    name?: string;
    technician_id?: string;
    profile_pic?: string;
  };
  status: {
    label: string;
    value: string;
    color_class: string;
  };
  started_at?: string;
  paused_at?: string;
  ended_at?: string;
  materials?: Array<Omit<MaterialFormattedPayload, 'material_cost'>>;
  media?: Array<MediaType>;
  quote_id?: string;
  reason?: string;
  last_modified_at?: string;
  is_active: boolean;
  trip_end_with?: { label: string; value: string };
  trip_end_with_type?: { label: string; value: string };
  is_rescheduled?: boolean;
  rescheduled_reason?: string;
  timer_paused_reason?: string;
  elapse_time_in_sec?: number;
  resumed_at?: string;
  disable_materials?: boolean;
  service_notes?: string;
  is_canceled?: boolean;
  en_route_at?: string;
  travel_time_in_sec?: number;
  work_performed?: { label: string; value: string };
  invoice_urls?: string[];
  trip_type?: 'lula' | 'in-house' | 'third-party-vendor';
  notified_vendor_count?: number;
  vendor_details?: {
    name?: string;
    vendor_id?: string;
    vendor_instructions?: string;
  };
  vendor_list?: Array<{
    name?: string;
    vendor_id?: string;
  }>;
  trips?: {
    appointment_id: string;
    category?: null | string;
    end_time?: null | string;
    is_active?: boolean;
    is_canceled?: boolean;
    is_rescheduled?: boolean;
    last_modified_at?: string;
    start_time?: null | string;
    status?: {
      value?: string;
      label?: string;
      color_class?: string;
    };
    paused_reason?: string;
    trip_number?: number;
    trip_type?: 'lula' | 'in-house' | 'third-party-vendor';
    wo_reference_number?: string;
    work_performed?: { value?: string; label?: string };
    elapse_time_in_sec?: number;
    estimated_return_start_time?: string;
    estimated_return_end_time?: string;
    rescheduled_reason?: string;
    canceled_reason?: string;
  }[];
  total_job_cost?: number;
};

export type ResidentInfoType = {
  resident_id: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  email: string;
  is_primary: boolean;
};

export type InvoiceItemSubsidiary = {
  cost_in_cents: number;
  cost_type?: 'per-unit' | 'total';
  description?: string;
  hourly_rate_in_cents?: number;
  hours?: number;
  invoice_item_subsidiary_id?: string;
  markup_fee?: number;
  markup_fee_type: string | null;
  markup_fee_type_value: number | null;
  minutes?: number;
  price?: number;
  quantity?: number;
  quantity_type?: string;
  quote_id?: string;
  quote_task_id?: string;
  quote_task_material_id?: string;
  subsidiary_type: string;
  total_cost_in_cents?: number;
  work_order_task_material_id?: string;
};
export type InvoiceLineItem = {
  invoice_line_item_id?: string;
  quote_task_id?: string;
  quote_id?: string;
  task_number?: number;
  total_material_cost_in_cents?: number;
  item_type: 'hourly-labor' | 'material' | 'quote-task' | 'service-notes' | 'no-show-fee' | 'labor';
  is_enabled?: boolean;
  service_notes?: string;
  total_markup_fee_in_cents?: number;
  total_material_amount_in_cents?: number;
  total_cost_in_cents?: number;
  total_labor_cost_in_cents?: number;
  description?: string;
  subsidiaries?: InvoiceItemSubsidiary[];
  hours?: number;
  minutes?: number;
  markup_fee_type?: string;
  markup_fee_type_value?: number;
  cost_in_cents?: number;
};

export type InvoiceTrip = {
  trip_id: string;
  trip_number: number;
  wo_reference_number?: number;
  trip_type: string;
  scheduled_start_time?: string;
  scheduled_end_time?: string;
  service_notes?: string;
  trip_status: {
    label: string;
    value: string;
  };
  total_cost_in_cents: number;
  media_details?: MediaType[];
  invoice_line_items?: InvoiceLineItem[];
};

export interface WorkOrderIssue {
  category: string;
  issue_id: string;
}

/**
 * A sibling work order as the WO details page reads it (BE `WorkOrderServiceRequestResource`).
 *
 * `can_assign` gates assigning a NEW issue here; `can_unassign` gates removing one already
 * assigned. They are not interchangeable - they diverge on Canceled.
 *
 * `can_unassign` is optional because the FE may ship ahead of the BE resource change; while
 * absent the unassign control stays hidden rather than offering an action BE would reject.
 */
export interface WorkOrder {
  work_order_id: string;
  work_order_number: string;
  issues: WorkOrderIssue[];
  can_assign: boolean;
  can_unassign?: boolean;
}

interface ServiceRequest {
  service_request_id: string;
  service_request_number: string;
  status: {
    label: string;
    value: string;
  };
  work_orders: WorkOrder[];
}

export type InvoiceDataObj = {
  invoice_id: string;
  invoice_number: string;
  amount_paid_in_cents: number;
  total_cost_in_cents: number;
  created_at: string;
  status: {
    value: string;
    label: string;
    color_class: string;
  };
  pdf_link?: string;
};

export type ResidentAvailabilityType = {
  date: string;
  timings: Array<{ [key: string]: boolean }>;
  isSaved: boolean;
};

export type RequestAvailabilityType = {
  date: string; // ISO Date YYYY-MM-DD
  timings: Array<{
    availability_request_uuid: string;
    timing: string; // "morning" | "afternoon" | "evening"
  }>;
};

export type ResidentAvailabilityAPIType = Omit<ResidentAvailabilityType, 'timings'> & {
  timings: string[];
};

export type NormalizedResidentAvailabilityType = {
  [date: string]: ResidentAvailabilityResponseType;
};

export type HealthScore = {
  classification: string;
  elapse_time?: string;
  elapse_time_in_sec?: string;
  slug?: string;
  weight?: string | null;
  created_at?: string;
};

export interface QuoteOption {
  option_id: string;
  description: string;
  option_type: 'replace' | 'repair';
  media: MediaType[];
  markup_fee_type: 'fixed' | 'percentage';
  labor_cost_in_cents?: string;
  markup_fee_type_value?: string;
  materials: Array<{
    material_id?: string;
    label: string;
    quantity: number;
    quantity_type: string;
    cost_type: 'per-unit' | 'total';
    cost_in_cents?: number;
    markup_fee_type?: 'fixed' | 'percentage';
    markup_fee_type_value?: string;
    total_cost_in_cents_including_markup?: number;
    unit_price_in_cents?: number;
    material_cost_in_cents?: number;
  }>;
}

export interface QuoteTask {
  quote_id?: string;
  quote_task_id: string;
  title: string;
  selected_option?: string;
  task_type: 'standard' | 'optional';
  issue?: {
    issue_id?: string;
    issue_title?: string;
  };
  options: QuoteOption[];
}

export type QuoteCustomerApprovalRecipient = {
  display_name: string;
  email: string;
  property_owner_uuid: string | null;
  external_source_id: string | null;
};

type QuoteCustomerApprovalContext = {
  approval_url?: string | null;
};

export type QuoteCustomerApproval = QuoteCustomerApprovalContext &
  (
    | {
        mode: 'external_connection';
        recipient: null;
        blocked_reason: null;
      }
    | {
        mode: 'property_owner' | 'legacy_resident';
        recipient: QuoteCustomerApprovalRecipient;
        blocked_reason: null;
      }
    | {
        mode: 'manual_link';
        recipient: null;
        blocked_reason: null;
      }
    | {
        mode: 'blocked';
        recipient: null;
        blocked_reason: string;
      }
  );

export type SendForCustomerApprovalResponse = {
  workOrderId?: string;
  quote_id?: string;
  status?: QuoteDetailsObj['status'];
  abilities?: QuoteDetailsObj['abilities'];
  url: string;
  customer_approval?: QuoteCustomerApproval;
};

export interface QuoteDetailsObj {
  abilities?: Array<'edit' | 'approve' | 'reject' | 'send_to_customer' | 'restore'>;
  status: {
    label: string;
    value: string;
  };
  quote_id: string;
  work_order_id?: string;
  time_zone?: string;
  quote_no: number;
  submitted_by: string;
  submitted_at: string;
  valid_until: string;
  approved_at?: string;
  approved_by?: string;
  rejected_by?: string | null;
  rejected_at?: string | null;
  reject_type?: 'reject_high_price' | 'reject_new_quote' | 'reject_other';
  submitted_user_type:
    | 'account_user'
    | 'technician'
    | 'resident'
    | 'asset_owner'
    | 'lula_pro_network'
    | 'vendor';
  reject_expected_price?: string;
  rejected_note?: string;
  tasks: QuoteTask[];
  reason?: string;
  address?: AddressInfo;
  pdf_link?: string;
  quote_total_in_cents?: string;
  quote_estimation_range: {
    max_in_cents: number;
    min_in_cents: number;
  };
  trip_fee_in_cents: number;
  default_trip_fee_in_cents?: number;
  trip_fee_type?: 'pay_trip_fee' | 'pay_if_rejected' | 'only_pay_if_rejected' | 'never_pay';
  is_lula_quote?: boolean;
  public_url?: string;
  customer_approval?: QuoteCustomerApproval;
}

export type CreateQuoteFormValues = {
  tasks: {
    quote_task_id?: string;
    title: string;
    selected_option?: string;
    task_type: 'standard' | 'optional';
    issue?: {
      issue_id?: string;
      issue_title?: string;
    };
    options: {
      option_id?: string;
      description: string;
      option_type: 'replace' | 'repair';
      media: MediaType[];
      uploadedMedia?: MediaType[];
      markup_fee_type?: 'fixed' | 'percentage';
      labor_cost_in_cents: string;
      markup_fee_type_value?: string | null;
      materials: Array<{
        material_id?: string;
        label: string;
        quantity: number;
        quantity_type: string;
        cost_type: 'per-unit' | 'total';
        cost_in_cents?: number;
        markup_fee_type_value?: string;
        markup_fee_type?: 'fixed' | 'percentage';
        total_cost_in_cents_including_markup?: number;
        isUnSaved?: boolean;
      }>;
    }[];
  }[];
  reason: string;
  mediaIdsToDeleteInEditMode?: string[];
};

// One entry per DISTINCT problem diagnosis on the work order — the same unit the
// all-skills technician rule counts. All four fields are non-nullable strings on the wire.
//
// `category` is effectively guaranteed non-blank: the BE sends
// `category ?: sub_category ?: diagnosis`, so it degrades to the most specific label
// available rather than to ''. `sub_category` and `diagnosis` have NO such fallback and CAN
// be empty strings when the taxonomy chain is incomplete — filter those on truthiness rather
// than on `!= null`.
export type RequiredSkill = {
  problem_diagnosis_id: string;
  category: string;
  sub_category: string;
  diagnosis: string;
};

export type WorkOrderDataObj = {
  abilities: Array<string>;
  work_order_id: string;
  work_order_number: string;
  description: string;
  created_by: string;
  invoices: {
    invoiceSource: { [key: string]: InvoiceDataObj };
    invoiceIds: Array<string>;
  };
  assignees: Array<{
    name: string;
    user_id: string;
    profile_pic: string;
  }>;
  status: {
    label: string;
    value: string;
    color_class: string;
  };
  due_date: string;
  priority: {
    value: string;
    label: string;
  };
  address?: AddressInfo;
  provider?: {
    type: 'in-house' | 'third-party-vendor' | 'lula' | 'not-assigned' | null;
    name: string | null;
  };
  category?: Array<{ label: string }>;
  resident_availabilities: {
    availability: Array<ResidentAvailabilityResponseType>;
    requested_at: string;
    display_availability_added_alert: boolean;
    availability_created_at: string;
    resident_added_availability_count: string;
    resident_availability_source: NormalizedResidentAvailabilityType;
    available_dates: string[];
  };
  resident_id: string;
  property: {
    property_id: string;
    address: AddressInfo;
    /** Legacy address values the edit form pre-fills from. */
    editable_address?: AddressInfo;
    /** True while the shown address comes from a building Google has verified. */
    address_verified?: boolean;
    name: string;
    state: {
      label: string;
      value: string;
    };
    location: {
      latitude: number;
      longitude: number;
    };
    residents: Array<ResidentInfoType>;
    access_information: {
      access_method: { value: string; label: string };
      access_code: string;
      access_note: string;
      pets?: { selected: { label: string; value: string }[]; other_detail?: string | null } | null;
      additional_notes?: string | null;
    };
    access_methods: Array<{
      label: string;
      value: string;
    }>;
  };
  timezone: string;
  tags: Array<string>;
  created_at: string;
  updated_at: string;
  group: string;
  media: Array<MediaType>;
  provider_name?: string;
  schedule_details: {
    previous_trip?: {
      name: string;
      provider_type: 'technician' | 'lula' | 'vendor';
      technician_id?: string;
      vendor_id?: string;
    } | null;
    recommended_provider_type: 'technician' | 'lula' | 'vendor';
    provider_settings: {
      technician: {
        recommended_duration: {
          min_estimated_mins: number | null;
          max_estimated_mins: number | null;
          estimated_mins: number | null;
          is_ai_recommended: boolean;
        };
        available_methods: Array<{
          label: string;
          value: SchedulingPreference;
          is_recommended: boolean;
        }>;
        available_durations: Array<number>;
        qualified_technicians: number;
        is_enabled: boolean;
        availableTechnicians: {
          technicians: Array<{ technician_uuid: string; full_name: string }>;
          // ranked_appointments: Array<{}>
          recommended_appointments: Array<{
            service_window_reference_id: string;
            technician_uuid: string;
            schedule_start_time: string;
            schedule_end_time: string;
          }>;
        };
      };
      vendor: {
        is_enabled: boolean;
        vendor_count: number;
      };
      lula: {
        is_enabled: boolean;
      };
    };
    work_order_issues: { work_order_issue_id: string }[];
    // OPTIONAL on purpose: extra-reducers casts a bare `{}` into schedule_details when the
    // context fetch rejects, so this key is genuinely absent at runtime. Read it with `?.`.
    required_skills?: RequiredSkill[];
  };
  trips: WorkOrderTrips;
  has_missing_data: boolean;
  show_schedule_details: boolean;
  health_score: HealthScore | null;
  nte_amount: {
    min_in_cents: number | null;
    max_in_cents: number | null;
    default_in_cents: number | null;
    nte_amount_in_cents: number;
    is_ai_recommended: boolean;
  };
  quotes: {
    quoteIds: Array<string>;
    quoteSource: { [key: string]: QuoteDetailsObj };
  };
  service_request: {
    serviceRequestId: string;
    serviceRequestNumber: string;
    status: {
      label: string;
      value: string;
    };
    workOrderIds: Array<string>;
    workOrders: {
      [key: string]: {
        work_order_id: string;
        work_order_number: string;
        issues: WorkOrderIssue[];
        can_assign: boolean;
        can_unassign?: boolean;
      };
    };
  };
  work_order_issues: Array<string>;
  nonEditableFields: (
    | 'address'
    | 'priority'
    | 'resident_info'
    | 'nte'
    | 'media'
    | 'issues'
    | 'resident_availability'
    | 'access_info'
  )[];
  is_scheduled_for_lula?: boolean;
  market?: {
    id: string;
    name: string;
  };
  out_of_scope_for_viewer?: boolean;
  // ORI-556: coordinator realized-cost total (foresight-invoice parity). Null + has_invoiced_cost=false
  // when there are no foresight invoices — an explicit empty state, never a misleading 0.
  total_invoice_amount_in_cents?: number | null;
  has_invoiced_cost?: boolean;
  // ORI-542: the vendor's NTE-overage justification, surfaced to the coordinator only when present.
  exceed_nte_reason?: string | null;
};

export interface WorkOrderAPIResponseObj
  extends Omit<
    WorkOrderDataObj,
    | 'tags'
    | 'service_request'
    | 'work_order_issues'
    | 'resident_availabilities'
    | 'trips'
    | 'quotes'
    | 'invoices'
  > {
  resident_availabilities: Omit<WorkOrderDataObj['resident_availabilities'], 'availability'> & {
    availability: ResidentAvailabilityAPIType[];
  };
  quotes: Array<QuoteDetailsObj>;
  tags: Array<TagData>;
  service_request: ServiceRequest;
  work_order_issues: Array<Issue>;
  trips: Array<TripSource>;
  invoices: Array<InvoiceDataObj>;
}

export type RightSection =
  | 'activityLog'
  | 'quote'
  | 'trips'
  | 'invoices'
  | 'workOrders'
  | 'chat'
  | 'chatDetails'
  | 'property';
export type LeftSection = 'woDetails';

export type CloseTripIssueObjType = {
  issue_id?: string;
  title?: string;
  description?: string;
  decline_reason?: string | null;
  service_notes?: string | null;
  issue_caused_by_resident?: boolean;
  media?: MediaType[];
  abilities?: string[];
  is_editable?: boolean;
  quote_task?: QuoteTask | null;
  materials?: MaterialObjTypeForApi[];
  status?: {
    value?: string;
    label?: string;
  };
  created_at?: string;
};

export type GetIssueDetailsApiResType = {
  issue_id: string;
  title: string;
  description: string;
  decline_reason: string;
  service_notes: string;
  materials: Array<{
    material_id: string;
    label: string;
    quantity: number;
    quantity_type: string;
    cost_type: 'per-unit' | 'total';
    cost_in_cents?: number;
    unit_price_in_cents?: number;
    total_cost_in_cents?: number;
    markup_fee_type?: 'fixed' | 'percentage' | null;
    markup_fee_type_value?: string | null;
    markup_fee_in_cents?: number;
  }>;
  media: MediaType[];
  abilities: Array<string>;
  is_editable: true;
  quote_task?: QuoteTask;
  trip_issue_status?: {
    value: string;
    label: string;
  };
  status: {
    value: string;
    label: string;
  };
};

export type InitialStateType = {
  isLoading: boolean;
  isUpdatingWorkOrderDetails: boolean;
  data: {
    data: string[];
    meta: MetaDataObj;
  };
  groupedData: {
    [key: string]: Array<string>;
  };
  groupedDataMeta: {
    [key: string]: GroupedDataMetaItem;
  };
  source: { [key: string]: WorkOrderDataObj };
  tagData: {
    data: string[];
    meta: MetaDataObj;
  };
  tagSource: {
    [key: string]: TagData;
  };
  showSidePanel: {
    needToRefetch?: boolean;
    isOpen?: boolean;
    workOrderUuid?: string;
    leftSection?: LeftSection | null;
    rightSection?: RightSection | null;
    mobileSection?: RightSection | null;
  } | null;
  /**
   * Single-SR resident-availability requests fired from a work order side panel that
   * have not settled yet. Keyed by service request id because that is what the
   * `serviceRequest.requestAvailability.completed` broadcast carries; the work order
   * the banner lives on is recorded alongside it (APX-607).
   */
  pendingAvailabilityRequests: {
    [serviceRequestId: string]: { workOrderId: string; previousRequestedAt: string };
  };
  currentScheduleDetails: {
    currentStep:
      | 'createTrip'
      | 'tripsListing'
      | 'closeTrip'
      | 'closeTripIssueExpandedView'
      | 'qualityCheck'
      | 'qualityCheckExpandedView'
      | 'createQuoteTask'
      | 'markAsUnresolved'
      | 'reviewQuote'
      | 'editQuoteTask'
      | 'rescheduleTrip'
      | 'cancelTrip'
      | null;
    tripId?: string;
    issueId?: string;
    shouldFetchCloseTripDetails?: boolean;
    shouldFetchIssueDetails?: boolean;
    closeTripData?: {
      quote_id?: string;
      appointment_type?: 'in-house' | 'lula' | 'third-party-vendor';
      issues?: {
        ids?: string[];
        source?: { [key: string]: CloseTripIssueObjType };
      };
      is_approved_quote_trip?: boolean;
      cost_break_down?: {
        labor: number;
        labor_time?: {
          duration_in_sec: number;
          cost_in_cents: number;
          labor_time_adjusted_by: string;
        };
        drive_time?: {
          cost_in_cents: number;
          drive_time_adjusted_by: string;
          duration_in_sec: number;
        };
        timer_adjusted_reason: string;
        total_material_in_cents: number;
        total_cost_in_cents: number;
      };
    };
  };
  isCloseTripIssueDetailsFetching: boolean;
  isMarkingIssueAsDone: boolean;
  currentQuoteStep: {
    step:
      | 'CreateQuote'
      | 'QuoteListing'
      | 'QuoteReview'
      | 'EditQuote'
      | 'QuoteHistoryTimeline'
      | 'QuoteRejection';
    quoteId?: string;
    shouldFetchQuoteDetails?: boolean;
    rejectQuoteData: {
      trip_fee_amount?: string;
      trip_fee_type?: 'pay_trip_fee' | 'pay_if_rejected' | 'never_pay';
    };
  } | null;
  currentInvoiceStep: {
    step: 'step_1' | 'step_2' | null;
    invoiceId?: string;
  } | null;
  currentFilterReferenceObj: { [key: string]: any };
  isFetchingWorkOrderDetails: boolean;
  // True when the WO details GET failed with no data to show, so the panel can render an
  // honest "couldn't load / Retry" state instead of a blank panel (ORI-604). Mirrors the SR
  // panel's `hasServiceRequestDetailsError`.
  hasWorkOrderDetailsError: boolean;
  // Per work order, bumped only when resident availability is known to have changed —
  // never by an initial load. The open schedule window watches it to reload its options.
  residentAvailabilityRevision: Record<string, number>;
  isFetchingScheduleDetails: boolean;
  isFetchingTechnicianForSchedule: boolean;
  isFetchingTechnicianSlotsForSchedule: boolean;
  isFetchingVendorSlotsForSchedule: boolean;
  isFetchingVendors: boolean;
  isSchedulingWorkOrder: boolean;
  isReSchedulingWorkOrder: boolean;
  isSendingQuoteForApproval: boolean;
  isCreatingQuote: boolean;
  isCreatingQuoteTask: boolean;
  isUpdatingQuoteTask: boolean;
  isFetchingTags: boolean;
  isDeletingQuoteTask: boolean;
  isRejectingOrApprovingQuote: boolean;
  isApprovingQuote: boolean;
  isDeletingWorkOrderMedia: boolean;
  isRejectingQuote: boolean;
  isUpdatingDriveTimeOrLaborTimeOrVendorCost: boolean;
  isClosingTrip: boolean;
  isFetchingQuoteDetails: boolean;
  currentStep: number;
  isFetchingCloseTripDetails: boolean;
  currentStepInMultiStepModal: number;
  isTimerStop: boolean;
  isFromSR: boolean;
  vendorInEditMode: {
    isEditingMode: boolean;
    editingVendorItemId: string[];
  };
  ownerQuote: {
    isApproving: boolean;
    isRejecting: boolean;
    quoteDetails: QuoteDetailsObj;
    /**
     * Published by a successful revalidation that actually replaced the quote,
     * carrying the snapshot it replaced. The reducer is the only place holding
     * both versions; the form consumes this once to reconcile its selections
     * and then clears it. `id` increments so two consecutive revalidations are
     * distinguishable even if the payloads are equal.
     */
    revalidation: {
      id: number;
      previous: QuoteDetailsObj;
    } | null;
  };
  shouldRefetchQuote?: boolean;
};

export const workOrderInitialState: InitialStateType = {
  isLoading: false,
  isUpdatingWorkOrderDetails: false,
  data: {
    data: [],
    meta: {
      current_page: 0,
      from: 0,
      last: '',
      next: '',
      per_page: 0,
      prev: '',
      to: 0,
      total: 0,
    },
  },
  groupedData: {},
  groupedDataMeta: {},
  source: {},
  pendingAvailabilityRequests: {},
  tagSource: {},
  tagData: {
    data: [],
    meta: {
      current_page: 0,
      from: 0,
      last: '',
      next: '',
      per_page: 0,
      prev: '',
      to: 0,
      total: 0,
    },
  },
  currentScheduleDetails: {
    currentStep: null,
    closeTripData: {},
  },
  isCloseTripIssueDetailsFetching: false,
  isMarkingIssueAsDone: false,
  currentQuoteStep: null,
  currentFilterReferenceObj: {},
  currentInvoiceStep: null,
  showSidePanel: null,
  isFetchingWorkOrderDetails: true,
  hasWorkOrderDetailsError: false,
  residentAvailabilityRevision: {},
  isFetchingTags: true,
  isFetchingScheduleDetails: false,
  isFetchingTechnicianForSchedule: true,
  isFetchingTechnicianSlotsForSchedule: false,
  isFetchingVendorSlotsForSchedule: false,
  isFetchingVendors: true,
  isSchedulingWorkOrder: false,
  isReSchedulingWorkOrder: false,
  isCreatingQuote: false,
  isSendingQuoteForApproval: false,
  isCreatingQuoteTask: false,
  isUpdatingQuoteTask: false,
  isDeletingQuoteTask: false,
  isRejectingOrApprovingQuote: false,
  isApprovingQuote: false,
  isRejectingQuote: false,
  isClosingTrip: false,
  isFetchingCloseTripDetails: false,
  isFetchingQuoteDetails: false,
  isDeletingWorkOrderMedia: false,
  isUpdatingDriveTimeOrLaborTimeOrVendorCost: false,
  currentStep: 0,
  currentStepInMultiStepModal: 0,
  isTimerStop: false,
  isFromSR: false,
  vendorInEditMode: {
    isEditingMode: false,
    editingVendorItemId: [],
  },
  ownerQuote: {
    isApproving: false,
    isRejecting: false,
    quoteDetails: {} as QuoteDetailsObj,
    revalidation: null,
  },
  shouldRefetchQuote: false,
};
